import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../../services/useAuth";
import { useReportFilters } from "../shared/useReportFilters";
import { toISO } from "../shared/utils";
import { API_URL } from "../shared/constants";

export function getStopDuration(l) {
  if (l.durationMinutes != null && l.durationMinutes > 0)
    return l.durationMinutes;
  if (l.endTime)
    return Math.round((new Date(l.endTime) - new Date(l.startTime)) / 60000);
  if (l.startTime)
    return Math.round((Date.now() - new Date(l.startTime)) / 60000);
  return 0;
}

export function useDowntimeReport() {
  const { getToken } = useAuth();
  const filters = useReportFilters();

  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [stopLogs, setStopLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );
  const addToast = useCallback(
    (msg, type = "success") =>
      setToasts((p) => [...p, { id: Date.now(), message: msg, type }]),
    [],
  );
  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        const [mR, sR] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, authH()),
          axios.get(`${API_URL}/api/shifts`, authH()),
        ]);
        setMachines(mR.data.machines || mR.data || []);
        setShifts(sR.data.shifts || []);
      } catch {}
    })();
  }, [authH]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("startDate", toISO(filters.startDate, false));
      p.set("endDate", toISO(filters.endDate, true));
      if (filters.machineIds.length > 0)
        p.set("machineIds", filters.machineIds.join(","));
      const r = await axios.get(
        `${API_URL}/api/reports/downtime?${p}`,
        authH(),
      );
      setStopLogs(r.data.stopLogs || []);
    } catch {
      addToast("Duruş verisi yüklenemedi", "danger");
      setStopLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate, filters.machineIds, authH, addToast]);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  const derived = useMemo(() => {
    if (!stopLogs.length) return null;

    const reasonMap = {},
      machineMap = {};
    stopLogs.forEach((l) => {
      const dur = getStopDuration(l);
      const sr = l.stopReason;
      const og = sr?.oeeGroup;

      const rk = sr ? String(sr._id) : "none";
      if (!reasonMap[rk])
        reasonMap[rk] = {
          name: sr?.name || "Nedensiz",
          color: og?.color || "#94a3b8",
          minutes: 0,
          count: 0,
        };
      reasonMap[rk].minutes += dur;
      reasonMap[rk].count++;

      const mn = l.machine?.name || "Bilinmeyen";
      if (!machineMap[mn]) machineMap[mn] = { id: l.machine?._id, minutes: 0, count: 0 };
      machineMap[mn].minutes += dur;
      machineMap[mn].count++;
    });

    const reasonArr = Object.values(reasonMap)
      .sort((a, b) => b.minutes - a.minutes)
      .slice(0, 15);

    const machineArr = Object.entries(machineMap)
      .sort((a, b) => b[1].minutes - a[1].minutes)
      .slice(0, 10);

    const paretoReasons = [...reasonArr].slice(0, 10);
    const paretoTotal = paretoReasons.reduce((s, r) => s + r.minutes, 0);
    let cumulative = 0;
    const paretoCumPcts = paretoReasons.map((r) => {
      cumulative += r.minutes;
      return paretoTotal > 0 ? Math.round((cumulative / paretoTotal) * 100) : 0;
    });

    const dayMap = {};
    const dayCountMap = {};
    stopLogs.forEach((l) => {
      const day = new Date(l.startTime).toISOString().slice(0, 10);
      dayMap[day] = (dayMap[day] || 0) + getStopDuration(l);
      dayCountMap[day] = (dayCountMap[day] || 0) + 1;
    });
    const dayKeys = Object.keys(dayMap).sort();

    return {
      machineArr,
      paretoReasons,
      paretoCumPcts,
      dayMap,
      dayKeys,
      dayCountMap,
    };
  }, [stopLogs]);

  return {
    filters,
    machines,
    shifts,
    stopLogs,
    loading,
    derived,
    fetchData,
    toasts,
    dismissToast,
  };
}
